import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'
import { emailContentModification, fetchAttachments, fetchEmailContent, fetchEmailTemplates } from '../OfferPackage/state/actions'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import EmailContentDisplay from './EmailContentDisplay'
import { useTranslation } from 'react-i18next'

interface EmailParameters {
  loading: boolean
  subject: string
  sender: string
  cc: string
  bcc: string
  emailBody: string
  modalOpen: boolean
  selectedItems: string[]
  tempSelectedItems: string[]
  emailTemplates: { EmailTemplateUUID: string; TemplateName: string }[]
  selectedTemplate: string
  placeholders: string[]
  emailAttachment: { EmailAttachmentUUID: string; EmailAttachmentName: string }[]
  emailTemplateUUID: string
  isSaveButtonDisabled: boolean
}

const EmailContent: React.FC = () => {
  const [emailParams, setEmailParams] = useState<EmailParameters>({
    loading: false,
    subject: '',
    sender: '',
    cc: '',
    bcc: '',
    emailBody: '',
    modalOpen: false,
    selectedItems: [],
    tempSelectedItems: [],
    emailTemplates: [],
    selectedTemplate: '',
    placeholders: [],
    emailAttachment: [],
    emailTemplateUUID: '',
    isSaveButtonDisabled: true
  })
  const dispatch = useDispatch()
  const loggedUserInfo = useSelector((state: IRootReducerState) => state.auth.user)
  const loggedUser = loggedUserInfo ? { role: loggedUserInfo['custom:role'], email: loggedUserInfo.email } : { role: null, email: null }
  const App_Id = useSelector((state: IRootReducerState) => state.auth?.user?.AppId)
  const { t } = useTranslation()

  useEffect(() => {
    const emailTemplates = async () => {
      setEmailParams(prev => ({ ...prev, loading: true }))
      try {
        const emailsTemplatesData = await dispatch(fetchEmailTemplates(App_Id || ''))
        setEmailParams(prev => ({ ...prev, emailTemplates: emailsTemplatesData.Data, loading: false }))
      } catch (error) {
        notifier.error(String(error))
        setEmailParams(prev => ({ ...prev, loading: false }))
      }
    }

    emailTemplates()
  }, [dispatch])

  const fetchAttachment = async () => {
    setEmailParams(prev => ({ ...prev, loading: true }))
    try {
      const fetchEmailData = await dispatch(fetchAttachments(App_Id || ''))
      setEmailParams(prev => ({ ...prev, emailAttachment: fetchEmailData.Data, loading: false }))
    } catch (error) {
      notifier.error(String(error))
      setEmailParams(prev => ({ ...prev, loading: false }))
    }
  }

  const emailTemplateContent = async (templateUUID: string) => {
    setEmailParams(prev => ({ ...prev, loading: true }))
    try {
      const emailContent = await dispatch(fetchEmailContent(templateUUID))
      setEmailParams(prev => ({
        ...prev,
        emailBody: emailContent.Data.EmailBody,
        emailTemplateUUID: emailContent.Data.EmailTemplateUUID,
        cc: emailContent.Data.EmailCC,
        bcc: emailContent.Data.EmailBCC,
        subject: emailContent.Data.EmailSubject,
        sender: emailContent.Data.EmailFrom,
        placeholders: emailContent.Data.EmailPlaceHolders,
        emailAttachment: [
          ...prev.emailAttachment,
          ...emailContent.Data.EmailAttachments.filter(
            (attachment: { EmailAttachmentName: string }) =>
              !prev.emailAttachment.some(existing => existing.EmailAttachmentName === attachment.EmailAttachmentName)
          )
        ],
        selectedItems: [
          ...new Set([
            ...prev.selectedItems,
            ...emailContent.Data.EmailAttachments.map((attachment: { EmailAttachmentName: string }) => attachment.EmailAttachmentName)
          ])
        ],
        loading: false
      }))
    } catch (error) {
      notifier.error(String(error))
      setEmailParams(prev => ({ ...prev, loading: false }))
    }
  }

  const handleTemplateChange = (event: any) => {
    const newTemplate = event.target.value
    const selectedTemplateData = emailParams.emailTemplates.find(template => template.TemplateName === newTemplate)
    setEmailParams(prev => ({
      ...prev,
      selectedTemplate: newTemplate,
      isSaveButtonDisabled: !selectedTemplateData,
      selectedItems: []
    }))
    if (selectedTemplateData) {
      fetchAttachment()
      emailTemplateContent(selectedTemplateData.EmailTemplateUUID)
    }
  }

  const handleModalOpen = () => {
    setEmailParams(prev => ({ ...prev, tempSelectedItems: prev.selectedItems, modalOpen: true }))
  }

  const handleModalClose = () => {
    setEmailParams(prev => ({ ...prev, tempSelectedItems: [], modalOpen: false }))
  }

  const handleItemToggle = (item: string) => {
    setEmailParams(prev => ({
      ...prev,
      tempSelectedItems: prev.tempSelectedItems.includes(item)
        ? prev.tempSelectedItems.filter(i => i !== item)
        : [...prev.tempSelectedItems, item]
    }))
  }

  const handleModalSave = () => {
    setEmailParams(prev => ({ ...prev, selectedItems: prev.tempSelectedItems, modalOpen: false }))
  }

  const handleItemDelete = (item: string) => {
    setEmailParams(prev => ({ ...prev, selectedItems: prev.selectedItems.filter(i => i !== item) }))
  }

  const handleSaveClick = async () => {
    setEmailParams(prev => ({ ...prev, loading: true }))

    const selectedAttachments = emailParams.emailAttachment
      .filter(attachment => emailParams.selectedItems.includes(attachment.EmailAttachmentName))
      .map(({ EmailAttachmentUUID, EmailAttachmentName }) => ({
        EmailAttachmentUUID,
        EmailAttachmentName
      }))

    const requestBody = {
      EmailAttachments: selectedAttachments,
      EmailBCC: emailParams.bcc,
      EmailBody: emailParams.emailBody,
      EmailCC: emailParams.cc,
      EmailFrom: emailParams.sender,
      EmailSubject: emailParams.subject,
      EmailTemplateUUID: emailParams.emailTemplateUUID,
      UpdatedByName: loggedUser?.email ?? ''
    }

    const missingFields = [
      !requestBody.EmailBody && t('labels.emailContent'),
      !requestBody.EmailFrom && t('labels.sender'),
      !requestBody.EmailSubject && t('labels.subject')
    ].filter(Boolean)

    if (missingFields.length > 0) {
      notifier.error(`${t('labels.missingFields')} ${missingFields.join(', ')}`)
      setEmailParams(prev => ({ ...prev, loading: false }))
      return
    }

    interface EmailContentModificationResponse {
      Data: {
        Status: string
        Message: string
      }
    }

    try {
      const emailContentModificationData = (await dispatch(
        emailContentModification(requestBody)
      )) as unknown as EmailContentModificationResponse
      if (emailContentModificationData.Data.Status === 'success') {
        notifier.success(t(emailContentModificationData.Data.Message))
      }
      setEmailParams(prev => ({ ...prev, loading: false }))
    } catch (error) {
      notifier.error(String(error))
      setEmailParams(prev => ({ ...prev, loading: false }))
    }
  }

  return (
    <EmailContentDisplay
      loading={emailParams.loading}
      selectedTemplate={emailParams.selectedTemplate}
      emailTemplates={emailParams.emailTemplates}
      handleTemplateChange={handleTemplateChange}
      subject={emailParams.subject}
      setSubject={(subject: string) => setEmailParams(prev => ({ ...prev, subject }))}
      sender={emailParams.sender}
      setSender={(sender: string) => setEmailParams(prev => ({ ...prev, sender }))}
      cc={emailParams.cc}
      setCc={(cc: string) => setEmailParams(prev => ({ ...prev, cc }))}
      bcc={emailParams.bcc}
      setBcc={(bcc: string) => setEmailParams(prev => ({ ...prev, bcc }))}
      emailBody={emailParams.emailBody}
      setEmailBody={(emailBody: string) => setEmailParams(prev => ({ ...prev, emailBody }))}
      modalOpen={emailParams.modalOpen}
      handleModalOpen={handleModalOpen}
      handleModalClose={handleModalClose}
      selectedItems={emailParams.selectedItems}
      tempSelectedItems={emailParams.tempSelectedItems}
      handleItemToggle={handleItemToggle}
      handleModalSave={handleModalSave}
      handleItemDelete={handleItemDelete}
      handleSaveClick={handleSaveClick}
      isSaveButtonDisabled={emailParams.isSaveButtonDisabled}
      emailAttachment={emailParams.emailAttachment}
      placeholders={emailParams.placeholders}
    />
  )
}

export default EmailContent
